if (sessionStorage.redirect) {
  const redirect = sessionStorage.redirect;
  sessionStorage.removeItem("redirect");
  window.history.replaceState(null, "", redirect);
}

import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Link,
} from "react-router-dom";
import axios from "axios";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Pagination, Navigation } from "swiper/modules";
import useIsMobile from "./hooks/useIsMobile";

import HomePage from "./pages/HomePage";
import LessonsPage from "./pages/LessonsPage";
import SurahPage from "./pages/SurahPage";
import SurahListPage from "./pages/SurahListPage";
import AlphabetLesson from "./pages/AlphabetLesson";
import LongVowelsLesson from "./pages/LongVowelsLesson";
import WordLesson from "./pages/WordLesson";
import JointLetters from "./pages/JointLetters";
import FlashcardsPage from "./pages/FlashcardsPage";
import ChaklLesson from "./pages/ChaklLesson";
import AnimalsLesson from "./pages/AnimalsLesson";
import AlphabetGame from "./pages/AlphabetGame";
import WordMatchGame from "./pages/WordMatchGame";
import ConversationalLesson from "./pages/ConversationalLesson";
import GamePage from "./pages/GamePage";
import KidsSongsPage from "./pages/KidsSongsPage";
import PrayerLessonsPage from "./pages/PrayerLessonsPage";
import PrayerStepsPage from "./pages/PrayerStepsPage";
import FemalePrayerStepsPage from "./pages/FemalePrayerStepsPage";
import ProphetStoriesPage from "./pages/ProphetStoriesPage";
import SignUpPage from "./pages/SignUpPage";
import LoginPage from "./pages/LoginPage";
import Footer from "./components/Footer";

// 🕌 Prayer Time Bar Component
function PrayerTimeBar() {
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async ({ coords }) => {
          const { latitude, longitude } = coords;
          try {
            const res = await axios.get(
              `https://api.aladhan.com/v1/timings?latitude=${latitude}&longitude=${longitude}&method=2`
            );
            setPrayerTimes(res.data.data.timings);
          } catch {
            setError("Couldn't fetch prayer times.");
          }
        },
        () => setError("Location access denied.")
      );
    } else {
      setError("Geolocation not supported.");
    }
  }, []);

  if (!prayerTimes || error) return null;

  const keys = ["Fajr", "Dhuhr", "Asr", "Maghrib", "Isha"];

  return (
    <div className="from-yellow-300 to-pink-400 text-black text-sm p-2 text-center shadow-md overflow-x-auto whitespace-nowrap">
      🕌 Prayer Times:&nbsp;
      {keys.map((k, i) => (
        <span key={k}>
          <strong>{k}:</strong> {prayerTimes[k]}
          {i < keys.length - 1 && " | "}
        </span>
      ))}
    </div>
  );
}

function MobileBottomNav() {
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow md:hidden flex justify-around py-2 z-50">
      <Link to="/" className="flex flex-col items-center text-yellow-700">
        <span className="text-xl">🏠</span>
        <span className="text-xs">Home</span>
      </Link>
      <Link to="/lessons" className="flex flex-col items-center text-yellow-700">
        <span className="text-xl">📚</span>
        <span className="text-xs">Lessons</span>
      </Link>
      <Link to="/game" className="flex flex-col items-center text-yellow-700">
        <span className="text-xl">🎮</span>
        <span className="text-xs">Games</span>
      </Link>
      <Link to="/prophet-stories" className="flex flex-col items-center text-yellow-700">
        <span className="text-xl">📖</span>
        <span className="text-xs">Prophets Stories</span>
      </Link>
    </div>
  );
}

function AppLayout({ children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const user = localStorage.getItem("activeUser");
  const isMobile = useIsMobile(); // <-- add this line

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowInstallPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  return (
    <div dir="rtl" className="min-h-screen flex flex-col bg-gradient-to-b from-yellow-50 to-pink-50">
      {/* Only show nav on desktop */}
      {!isMobile && (
        <nav className="bg-gradient-to-r from-pink-400 to-yellow-300 p-4 shadow-lg">
          <div className="container mx-auto flex flex-row-reverse justify-between items-center">
            <Link to="/" className="text-2xl font-bold text-white hover:text-yellow-100 transition-colors">
              🌙 Learn Arabic & Quran 🌟
            </Link>
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-4 space-x-reverse">
              <NavLinks user={user} />
            </div>
            {/* Hamburger Menu Button */}
            <button
              className="md:hidden text-white text-3xl focus:outline-none"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              ☰
            </button>
          </div>
          {/* Mobile Menu */}
          {menuOpen && (
            <div className="md:hidden flex flex-col space-y-2 mt-4">
              <NavLinks user={user} isMobile />
            </div>
          )}
        </nav>
      )}

      {/* PWA Install Prompt */}
      {showInstallPrompt && (
        <div className="fixed bottom-4 right-4 bg-white p-4 rounded-lg shadow-lg z-50">
          <p className="text-gray-800 mb-2">Install our app for a better experience!</p>
          <div className="flex space-x-2">
            <button
              onClick={handleInstallClick}
              className="bg-green-500 text-white px-4 py-2 rounded-full hover:bg-green-600 transition-colors"
            >
              Install
            </button>
            <button
              onClick={() => setShowInstallPrompt(false)}
              className="bg-gray-300 text-gray-800 px-4 py-2 rounded-full hover:bg-gray-400 transition-colors"
            >
              Later
            </button>
          </div>
        </div>
      )}

      <main className="flex-grow pb-16 md:pb-0">{children}</main>
      <div className="hidden md:block">
        <Footer />
      </div>
      <MobileBottomNav />
    </div>
  );
}

// Extracted NavLinks for cleaner JSX
function NavLinks({ user, isMobile }) {
  const linkClass = "bg-white text-yellow-700 px-4 py-2 rounded-full shadow hover:bg-yellow-100 transition-all";

  return (
    <div className={`flex ${isMobile ? "flex-col space-y-2" : "items-center space-x-3 space-x-reverse"}`}>
      <Link to="/game" className={linkClass}>
        Play Games
      </Link>
      <Link to="/lessons" className={linkClass}>
        Start Learning
      </Link>
      <Link to="/prophet-stories" className={linkClass}>
        Prophet Stories
      </Link>
      {user ? (
        <>
          <span className="text-white font-bold">👤 {user}</span>
          <button
            onClick={() => {
              localStorage.removeItem("activeUser");
              window.location.href = "/login";
            }}
            className={linkClass}
          >
            Logout
          </button>
        </>
      ) : (
        <Link to="/login" className={linkClass}>
          Login
        </Link>
      )}
    </div>
  );
}

function App() {
  const isMobile = useIsMobile();

  return (
    <Router>
      <AppLayout>
        <Routes>
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={isMobile ? <LessonsPage /> : <HomePage />} />
          <Route path="/lessons" element={<LessonsPage />} />
          <Route path="/lessons/prayers" element={<PrayerLessonsPage />} />
          <Route path="/lessons/surahs" element={<SurahListPage />} />
          <Route path="/lessons/alphabet" element={<AlphabetLesson />} />
          <Route path="/lessons/conversation" element={<ConversationalLesson/>} />
          <Route path="/lessons/joint" element={<JointLetters />} />
          <Route path="/game" element={<GamePage />} />
          <Route path="/prophet-stories" element={<ProphetStoriesPage />} />
          <Route path="/lessons/surahs/:surahId" element={<SurahPage />} />
          <Route path="/alphabetLesson" element={<AlphabetLesson />} />
          <Route path="/lessons/longvowels" element={<LongVowelsLesson />} />
          <Route path="/lessons/animals" element={<AnimalsLesson />} />
          <Route path="/lessons/words" element={<WordLesson />} />
          <Route path="/game/flashcards" element={<FlashcardsPage />} />
          <Route path="/game/word-match" element={<WordMatchGame />} />
          <Route path="/lessons/chakl" element={<ChaklLesson />} />
          <Route path="/game/alphabet" element={<AlphabetGame />} />
          <Route path="/prayers" element={<PrayerLessonsPage />} />
          <Route path="/prayer/steps" element={<PrayerStepsPage />} />
          <Route path="/songs" element={<KidsSongsPage />} />
          <Route path="/prayer/steps/female" element={<FemalePrayerStepsPage />} />
        </Routes>
      </AppLayout>
    </Router>
  );
}

export default App;
